import React, { useMemo } from "react";
import { Helmet } from "react-helmet";

const SITE_NAME = "León Gallery";
const SITE_URL = "https://leon-gallery.com";
const DEFAULT_IMAGE = `${SITE_URL}/static/media/dddd.f96bbcbee8c8c2f5e3ee.png`;

const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();

const truncate = (value, max = 160) => {
  const text = stripHtml(value);
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trim()}…`;
};

const absoluteUrl = (value) => {
  if (!value) return "";
  try {
    return new URL(value, SITE_URL).href;
  } catch {
    return "";
  }
};

function ArtworkSEO({
  title,
  artist,
  description,
  image,
  lotNumber,
  auctionName,
  auctionSlug,
  auctionId,
  canonical,
  noindex = false,
  privateSale = false,
}) {
  const seo = useMemo(() => {
    const cleanTitle = stripHtml(title);
    const cleanArtist = stripHtml(artist);
    const cleanAuction = stripHtml(auctionName);
    const cleanDescription = stripHtml(description);

    const artworkTitle = cleanTitle || "Artwork";
    const titleParts = [
      cleanArtist ? `${cleanArtist} – ${artworkTitle}` : artworkTitle,
      lotNumber ? `Lot ${lotNumber}` : "",
    ].filter(Boolean);

    const resolvedTitle = `${titleParts.join(" | ")} | ${SITE_NAME}`;

    const descriptionParts = [
      cleanArtist ? `Artwork by ${cleanArtist}.` : "",
      cleanDescription,
      lotNumber ? `Lot ${lotNumber}.` : "",
      cleanAuction ? `Featured in ${cleanAuction}.` : "",
      privateSale ? "Available for private sale at León Gallery." : "Available through León Gallery's auction program.",
    ].filter(Boolean);

    const resolvedDescription = truncate(descriptionParts.join(" ")) ||
      `Explore ${artworkTitle} at ${SITE_NAME}, a leading Philippine art gallery and auction house.`;

    const resolvedImage = absoluteUrl(image) || DEFAULT_IMAGE;
    const resolvedCanonical = canonical
      ? absoluteUrl(canonical)
      : typeof window !== "undefined"
        ? `${SITE_URL}${window.location.pathname}`
        : SITE_URL;

    const itemUrl = resolvedCanonical;
    const creator = cleanArtist
      ? {
          "@type": "Person",
          name: cleanArtist,
        }
      : undefined;

    const artwork = {
      "@type": "VisualArtwork",
      name: artworkTitle,
      image: [resolvedImage],
      description: resolvedDescription,
      url: itemUrl,
      ...(creator ? { creator } : {}),
      ...(lotNumber ? { identifier: `Lot ${lotNumber}` } : {}),
      isPartOf: {
        "@type": privateSale ? "CollectionPage" : "WebPage",
        name: cleanAuction || (privateSale ? "León Gallery Private Sale" : "León Gallery Auction"),
        url: cleanAuction && auctionSlug && auctionId
          ? `${SITE_URL}/auction/${String(auctionSlug).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "")}/${auctionId}`
          : SITE_URL,
      },
    };

    const breadcrumbItems = [
      { name: "Home", item: `${SITE_URL}/` },
      privateSale
        ? { name: "Private Sales", item: `${SITE_URL}/private-sales` }
        : { name: "Auctions", item: `${SITE_URL}/auctions` },
      cleanAuction ? { name: cleanAuction, item: SITE_URL } : null,
      { name: artworkTitle, item: itemUrl },
    ].filter(Boolean);

    return {
      title: resolvedTitle,
      description: resolvedDescription,
      image: resolvedImage,
      canonical: resolvedCanonical,
      structuredData: [
        {
          "@context": "https://schema.org",
          "@type": "VisualArtwork",
          ...artwork,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbItems.map((item, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: item.name,
            item: item.item,
          })),
        },
      ],
    };
  }, [title, artist, description, image, lotNumber, auctionName, auctionSlug, auctionId, canonical, noindex, privateSale]);

  return (
    <Helmet>
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={seo.canonical} />

      <meta property="og:type" content="article" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.canonical} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:alt" content={stripHtml(title)} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={stripHtml(title)} />

      {seo.structuredData.map((schema, index) => (
        <script key={`seo-schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default ArtworkSEO;
